import { useRef, useState } from 'react';
import {
  apiErrorSummary,
  isWorkSession,
  NOMINAL_COUNTED_BODYWEIGHT_KG,
  saveErrorKey,
  splitWorkout,
  type AthleteEnergy,
  type EffortScale,
  type Exercise,
  type PRsByExercise,
} from '@heraion/shared';

import { weightUnitLabels } from '../weightUnitLabels';
import { clearPersistedLoggerState } from './state/loggerDraftStorage';
import type {
  LoggerDispatch,
  LoggerDraft,
  WeightUnit,
} from './state/loggerDraft';
import { invalidateEntryHistoryCache } from './useEntryHistory';
import { detectOutliersForDraft } from './workoutMapping';
import { outlierPromptItems } from './outlierPromptItems';
import type { LoggerMode } from './loggerMode';
import type { EditingTarget, LoggerOnSaved } from './LoggerScreen';
import type { LoggerHostDeps } from './hostDeps';
import { useFlushNoteEvents } from './useFlushNoteEvents';
import { saveErrorText, saveFailureMessage } from './saveWriter';
import {
  buildSavePayload,
  saveDraft,
  type BuiltPayload,
  type SaveDraftOutcome,
} from './saveDraft';
import type { PostSaveSummaryState } from './usePostSaveSummary';

type SentOutcome = Extract<SaveDraftOutcome, { kind: 'sent' }>;
type QueuedOutcome = Extract<SaveDraftOutcome, { kind: 'queued' }>;

/** The idle watch's save: closed (saved as `sessionId`, or null with the
 *  draft kept), or busy behind the athlete's own save. */
export type AutoSaveResult = { kind: 'busy' } | { kind: 'closed'; sessionId: string | null };

// Save flow extracted verbatim out of LoggerScreen (Track R slice A).
// Owns the `submitting` flag + the proceed/onSave logic; the error,
// serverError, and postSaveSummary states stay in LoggerScreen (read by
// the render tree and cleared by the hydration effect) and are written
// through the setters passed in here. Behaviour-preserving — no path
// changed, only relocated.

const GONE_KEY = 'mobile.logger.errCorrectionGone';
/** The host-with-no-queue wording: points at the button, not at the phone. */
export const GONE_HERE_KEY = 'mobile.logger.errCorrectionGoneHere';


export type UseSaveWorkoutParams = {
  draft: LoggerDraft;
  editing: EditingTarget | null;
  mode: LoggerMode;
  catalog: Exercise[] | null;
  prHistory: PRsByExercise | null;
  defaultUnit: WeightUnit;
  effortScale: EffortScale;
  /** The athlete's energy demographics (bodyweight/age/sex), resolved from the
   *  Profile by the caller (resolveAthleteEnergy). Feeds the HC-5 workout
   *  calorie estimate persisted at save; absent fields degrade it honestly. */
  athlete: AthleteEnergy;
  /** i18n.resolvedLanguage — used for the outlier-prompt weight format. */
  resolvedLanguage: string | undefined;
  /** Auto-name suggestion ([[workoutNameSuggest]]): the fallback persisted
   *  as Workout.name when the user left the name blank. */
  nameSuggestion: string;
  dispatch: LoggerDispatch;
  persistedHydratedRef: { current: boolean };
  onSaved: LoggerOnSaved;
  onClose: () => void;
  /** recordsResource.refresh — refreshes the cached PR/records view. */
  refreshRecords: () => unknown;
  /** useStopAllTimers: stop every timer (rest, hold, bout) and its
   *  notifications when the workout ends. The sent-save path
   *  keeps the modal open in summary mode and defers the draft reset
   *  to the Done CTA, so the session-key effect (which tears the
   *  timer down on discard/queued) never fires here — the timer would
   *  otherwise keep ticking under the summary card. */
  stopAllTimers: () => void;
  setError: (v: string | null) => void;
  setServerError: (v: string | null) => void;
  setPostSaveSummary: (v: PostSaveSummaryState | null) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  deps: LoggerHostDeps;
};

export function useSaveWorkout({
  draft,
  editing,
  mode,
  catalog,
  prHistory,
  defaultUnit,
  effortScale,
  athlete,
  resolvedLanguage,
  nameSuggestion,
  dispatch,
  persistedHydratedRef,
  onSaved,
  onClose,
  refreshRecords,
  stopAllTimers,
  setError,
  setServerError,
  setPostSaveSummary,
  t,
  deps,
}: UseSaveWorkoutParams) {
  const [submitting, setSubmitting] = useState(false);
  // Ref guard: a double tap lands before `submitting` re-renders.
  const inFlight = useRef(false);
  const unitLabels = weightUnitLabels(t);

  // The timeline notes, written once the workout is saved (#24).
  const flushNoteEvents = useFlushNoteEvents({ draft, editing, deps });

  const isWorkWorkout = (built: BuiltPayload): boolean => {
    const bySlug = new Map((catalog ?? []).map((ex) => [ex.slug, ex]));
    const { sets } = splitWorkout({ ...built.payload, id: 'work-check' });
    return isWorkSession(sets, (slug) => bySlug.get(slug), athlete?.weightKg ?? NOMINAL_COUNTED_BODYWEIGHT_KG);
  };

  // Persist a validated payload to the server (or the outbox on a
  // genuine network failure), through saveDraft (#445). Split out of onSave
  // so the outlier-warning Alert can re-enter the save flow when the user
  // picks "Save anyway": buildPayload re-validation would be redundant by
  // then, the draft is the same as when the prompt opened.
  const proceedWithSave = async (built: BuiltPayload, asNew = false) => {
    setSubmitting(true);
    try {
      const outcome = await saveDraft(built, {
        draft, editing, mode, asNew, athlete, deps, flushNoteEvents,
      });
      switch (outcome.kind) {
        case 'notSignedIn':
        case 'notQueued':
          setError(outcome.error);
          break;
        case 'queued':
          await afterQueued(outcome);
          break;
        case 'sent':
          await afterSent(outcome);
          break;
        case 'refused':
          showRefusal(outcome.err);
          break;
      }
    } catch (err) {
      // #445: what throws once the workout is sent no longer queues it a
      // second time; it reaches the athlete as a refusal does.
      showRefusal(err);
    } finally {
      setSubmitting(false);
    }
  };

  // A refusal the athlete can act on reads as a sentence (#38).
  const showRefusal = (err: unknown) => {
    const key = saveErrorKey(err);
    if (key === GONE_KEY && deps.offerSaveAsNewWhenGone) setError(GONE_HERE_KEY);
    else if (key) setError(key);
    else setServerError(apiErrorSummary(err));
  };

  // The workout is sent or queued and nothing waits for a summary: the
  // draft goes and the logger closes. The queued path, and the idle watch's.
  const finishAndClose = async (outcome: SentOutcome | QueuedOutcome) => {
    dispatch({ type: 'reset' });
    stopAllTimers();
    // ⭐ LG-76: awaited, same reason as the Done CTA below.
    await clearPersistedLoggerState();
    persistedHydratedRef.current = false;
    // Queued saves don't reach the server yet, but the draft is
    // gone from the UI: invalidate so the next Logger open
    // doesn't carry stale "this slug has no history" memory if
    // the outbox drains in between.
    invalidateEntryHistoryCache();
    if (outcome.kind === 'sent') {
      deps.invalidateSessionResults();
      void refreshRecords();
    }
    // LG-35: the MAX prompt rides the offline path too. It used to
    // fire without maxBumpInput, so evaluateAfterSave bailed and a
    // workout logged in airplane mode never offered a new MAX at all
    // and the athlete had to notice and set it by hand, or lose it.
    // The evaluation reads the in-memory maxes cache, so it works
    // whenever the app has loaded them at some point this session;
    // a cold boot with no signal still skips it, deliberately (see
    // the maxesLoaded guard in useMaxBumpFlow).
    // Into a recovery card, not the queue (#314): no MAX prompt for a
    // workout that did not land, and the athlete is told where it is.
    const carded = outcome.kind === 'queued' && outcome.carded;
    onSaved(outcome.kind, carded ? undefined : outcome.maxBumpInput);
    if (carded) deps.notify(t('mobile.recovery.title'), t('mobile.logger.correctionToBell'));
    onClose();
  };

  // The queued path: the draft goes, the queue keeps the save.
  const afterQueued = async (outcome: QueuedOutcome) => {
    try {
      await finishAndClose(outcome);
    } catch (err) {
      setError(saveFailureMessage(err));
    }
  };

  const afterSent = async ({ sessionId, maxBumpInput, failedNotes }: SentOutcome) => {
    // ⭐ LG-53: the persisted slot stops representing unsaved work HERE,
    // not at the Done CTA below. It used to survive until the athlete
    // tapped Done, so a process death in between left a draft with
    // inProgress:true; the cold-boot path offered Resume, the athlete
    // saved again, and the save minted a FRESH uuidv4 that walked
    // straight past the API's attribute_not_exists guard. Three duplicate
    // pairs reached production that way. The clear is placed after the
    // note flush (in saveDraft) on purpose: until the notes are in their
    // own outbox the slot is still the only copy of them.
    // ⛔ The mirror in useDraftPersistence would put the blob right back
    // (the modal stays visible in summary mode), so this clear only holds
    // because that effect (and the background flush beside it) now return
    // early once postSaveSummary is set. One without the other is cosmetic.
    await clearPersistedLoggerState();
    // Stay open in summary mode. Defer the reset + persistence
    // teardown + onClose to the Done CTA so the PostSessionSummaryCard
    // can read the draft-less server state via useSessionResult.
    // invalidateEntryHistoryCache + recordsResource.refresh run now
    // so the cached views the user navigates to (after Done) already
    // reflect this save. onSaved fires here too: the workouts-list
    // refresh is independent of the user's summary-mode dismissal.
    invalidateEntryHistoryCache();
    // The server recomputes this session's SessionResult on save, so drop
    // the shared progress cache too: the Progress panels refetch fresh
    // when next focused (perf B2).
    deps.invalidateSessionResults();
    void refreshRecords();
    // Workout ended: stop the rest timer now (banner + notification),
    // not at the deferred Done CTA, so it doesn't keep ticking under
    // the summary card.
    stopAllTimers();
    const maxProposed = onSaved('sent', maxBumpInput) ?? [];
    setPostSaveSummary({
      sessionId,
      maxProposed,
      // #511: only here. A queued save skips the summary, so its failed
      // notes (also in the outcome) have no line to show.
      failedNotes,
      // ⭐ LG-76: awaited before onClose. Closing lets the next workout open,
      // and a plan start now READS the slot before it seeds. An unawaited
      // clear can leave this saved draft there to be resurrected over it.
      // ⛔ The clear below is NOT redundant with LG-53's above: the reset
      // dispatch on the line before it changes the draft, which re-fires
      // the mirror effect. It returns early today because postSaveSummary
      // is still set at that instant; this awaited clear is what keeps
      // the slot empty if that ordering ever stops holding.
      onDone: () => {
        void (async () => {
          dispatch({ type: 'reset' });
          await clearPersistedLoggerState();
          persistedHydratedRef.current = false;
          setPostSaveSummary(null);
          onClose();
        })();
      },
    });
  };

  const runSave = async (asNew: boolean) => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      await runSaveOnce(asNew);
    } finally {
      inFlight.current = false;
    }
  };

  const runSaveOnce = async (asNew: boolean) => {
    setError(null);
    setServerError(null);
    // Drop any set the athlete never marked done. The timeline FocusPanel
    // (the sole logger) always shows a "next set" input that may never be
    // LOG-SET; that proposed-but-not-done set must not be saved (Antenore
    // 2026-05-30; design §7.3).
    const built = buildSavePayload(draft, effortScale, nameSuggestion);
    if (!built.ok) {
      // Surface via Alert too: the header shows the error beside Save
      // (#45), and the Alert makes sure a validation failure is read.
      setError(built.reason);
      deps.notify(t('workout.savePromptTitle'), saveErrorText(built.reason, t));
      return;
    }
    // #536 / #51: a session that was not real work (isWorkSession, shared)
    // asks before it is saved; saved anyway, it stays out of the stats. The
    // way out is "Back to workout"; discarding is the logger's own ✕. Not on a
    // correction (the workout is already saved) nor on a past log.
    if (!editing && mode !== 'log-past' && !isWorkWorkout(built)) {
      const keep = await deps.confirm({
        title: t('workout.savePromptTitle'),
        body: t('workout.notWorkPromptBody'),
        confirmLabel: t('workout.savePromptAnyway'),
        cancelLabel: t('workout.savePromptContinue'),
      });
      if (!keep) return;
    }
    // Outlier-weight prompt at save time. Two tiers (1.45.0):
    // history-based (3× the lifter's recent best for that exercise,
    // from /api/me/records — same aggregate driving the gold-pill PR
    // check) and an absolute equipment-ceiling fallback that catches
    // brand-new exercises where prHistory has nothing to compare
    // against. The 999 kg DB row that the old gate let through
    // silently now trips the dumbbell ceiling. Edit mode passes a
    // null prHistory by design (avoids self-comparison false
    // negatives on the PR check); the absolute fallback still fires
    // there so a typo in an edit is caught the same way.
    const outliers = detectOutliersForDraft(draft, prHistory, catalog);
    if (outliers.length > 0) {
      const locale = resolvedLanguage ?? 'en';
      const items = outlierPromptItems(outliers, t, locale, defaultUnit, unitLabels);
      const message = `${t('workout.outlierPromptBody', {
        count: outliers.length,
      })}\n\n• ${items}`;
      const proceedAnyway = await deps.confirm({
        title: t('workout.savePromptTitle'),
        body: message,
        confirmLabel: t('workout.savePromptAnyway'),
        cancelLabel: t('workout.savePromptContinue'),
      });
      if (proceedAnyway) await proceedWithSave(built, asNew);
      return;
    }
    await proceedWithSave(built, asNew);
  };

  // #445: the idle watch's save and close, with nobody at the phone. What
  // differs from onSave, on purpose: no outlier prompt (nobody to answer
  // it), no summary card, and the logger closes. Nothing completed, or a
  // save that went nowhere: the draft stays, and the logger closes all the
  // same. Busy: the athlete's own save is running, and decides.
  const autoSave = async (): Promise<AutoSaveResult> => {
    if (inFlight.current) return { kind: 'busy' };
    inFlight.current = true;
    setSubmitting(true);
    try {
      const built = buildSavePayload(draft, effortScale, nameSuggestion);
      const outcome = built.ok
        ? await saveDraft(built, {
            draft, editing, mode, asNew: false, athlete, deps, flushNoteEvents,
          })
        : null;
      if (outcome?.kind === 'sent' || outcome?.kind === 'queued') {
        try {
          await finishAndClose(outcome);
        } catch (err) {
          // Saved all the same: the athlete is still told.
          console.warn('[idle] saved, but the logger did not close cleanly', err);
        }
        return { kind: 'closed', sessionId: outcome.sessionId };
      }
      stopAllTimers();
      // The next open reads the slot again, as after a cold start.
      persistedHydratedRef.current = false;
      onClose();
      return { kind: 'closed', sessionId: null };
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return {
    submitting,
    onSave: () => runSave(false),
    onSaveAsNew: () => runSave(true),
    autoSave,
  };
}
